import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Alert, Button } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import ContentLayout from '../../users/components/ContentLayout';
import TableSection from '../../users/components/TableSection';
import ReservedTherapiesTable from '../components/ReservedTherapiesTable';
import ReservedTherapiesModal from '../components/modals/ReservedTherapiesModal';
import { fetchReservedTherapies } from '../api/reservedTherapiesApi';
import useTableSearch from '../../../shared/hooks/useTableSearch';
import MedicationFilterSelect, {
  DEFAULT_MEDICATION_MATCH,
} from '../../medications/components/MedicationFilterSelect';
import { PAGE_SIZE } from '../../../config';
import TotalCountField from '../../../components/elements/TotalCountField';

const emptyState = {
  icon: '/assets/icons/no-users.svg',
  title: 'Trenutno nema rezervisanih terapija',
};

export default function ReservedTherapiesPage() {
  // Jedinstvena pretraga (Pacijent/LBO/BZK) - src/config/searchConfig.js.
  const tableSearch = useTableSearch('reservedTherapies');
  const { reset: resetTableSearch } = tableSearch;
  const [selectedMedications, setSelectedMedications] = useState([]);
  const [medicationMatch, setMedicationMatch] = useState(
    DEFAULT_MEDICATION_MATCH
  );
  const [startDateSearch, setStartDateSearch] = useState('');
  const [endDateSearch, setEndDateSearch] = useState('');

  const [patients, setPatients] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(null);
  const [totalReserved, setTotalReserved] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [message, setMessage] = useState('');

  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isPatientModalOpen, setIsPatientModalOpen] = useState(false);

  const latestRequestRef = useRef(0);

  const {
    userSearch: debouncedUserSearch,
    lboSearch: debouncedLboSearch,
    bzkSearch: debouncedBzkSearch,
  } = tableSearch.params;

  useEffect(() => {
    setCurrentPage(1);
  }, [
    debouncedUserSearch,
    debouncedLboSearch,
    debouncedBzkSearch,
    selectedMedications,
    medicationMatch,
    startDateSearch,
    endDateSearch,
  ]);

  const loadReservedTherapies = useCallback(async () => {
    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;
    setListLoading(true);

    const {
      data,
      ok,
      message: responseMessage,
    } = await fetchReservedTherapies({
      page: currentPage,
      limit: PAGE_SIZE,
      userSearch: debouncedUserSearch || null,
      lboSearch: debouncedLboSearch || null,
      bzkSearch: debouncedBzkSearch || null,
      medications: selectedMedications,
      medicationMatch,
      startDate: startDateSearch || null,
      endDate: endDateSearch || null,
    });

    if (requestId !== latestRequestRef.current) return;

    setListLoading(false);

    if (!ok) {
      setMessage(
        responseMessage ||
          'Doslo je do greske pri dobavljanju rezervisanih terapija.'
      );
      return;
    }

    setMessage('');

    const rows = data?.patients || data?.data?.patients || [];
    const pagination = data?.pagination || data?.data?.pagination || {};

    setPatients(rows);
    setTotalPages(pagination?.totalPages || 1);
    setTotalResults(pagination?.totalCount ?? null);
    setTotalReserved(
      data?.totalReservedOccurrences ??
        data?.data?.totalReservedOccurrences ??
        null
    );

    const resolvedPage = pagination?.currentPage || 1;
    if (resolvedPage !== currentPage) {
      setCurrentPage(resolvedPage);
    }
  }, [
    currentPage,
    debouncedBzkSearch,
    debouncedLboSearch,
    debouncedUserSearch,
    endDateSearch,
    selectedMedications,
    medicationMatch,
    startDateSearch,
  ]);

  useEffect(() => {
    loadReservedTherapies();
  }, [loadReservedTherapies]);

  // Modal sa detaljima drzi selektovanog pacijenta iz poslednjeg ucitavanja liste.
  useEffect(() => {
    if (!isPatientModalOpen || !selectedPatient?.userId) return;

    const refreshedPatient = patients.find(
      (patient) => patient?.userId === selectedPatient.userId
    );

    if (refreshedPatient && refreshedPatient !== selectedPatient) {
      setSelectedPatient(refreshedPatient);
    }
  }, [isPatientModalOpen, patients, selectedPatient]);

  const hasActiveFilters = Boolean(
    tableSearch.isActive ||
      selectedMedications.length ||
      startDateSearch ||
      endDateSearch
  );

  const handleResetFilters = () => {
    resetTableSearch();
    setSelectedMedications([]);
    setMedicationMatch(DEFAULT_MEDICATION_MATCH);
    setStartDateSearch('');
    setEndDateSearch('');
  };

  const handleDateRangeChange = useCallback((startDate, endDate) => {
    setStartDateSearch(startDate);
    setEndDateSearch(endDate);
  }, []);

  const dateFilter = useMemo(
    () => ({
      type: 'dateRange',
      value: [startDateSearch, endDateSearch],
      placeholder: ['Datum od', 'Datum do'],
      onChange: handleDateRangeChange,
      className: 'therapyFilterDate',
    }),
    [endDateSearch, handleDateRangeChange, startDateSearch]
  );

  const handlePatientClick = (patient) => {
    setSelectedPatient(patient);
    setIsPatientModalOpen(true);
  };

  const handlePatientModalClose = () => {
    setIsPatientModalOpen(false);
    setSelectedPatient(null);
  };

  return (
    <>
      <ContentLayout>
        {!!message && (
          <Alert
            title={message}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        <TableSection
          // Za loader tabele (da li jos nema redova).
          data={patients}
          showCreateButton={false}
          searchFieldOrder={[
            'tableSearch',
            'tertiarySearch',
            'quaternarySearch',
          ]}
          inlineHeaderActions={
            hasActiveFilters ? (
              <Button
                type="default"
                className="btn-reset-filters"
                onClick={handleResetFilters}
              >
                <span className="btn-reset-filters__content">
                  Ponisti filtere
                  <CloseOutlined />
                </span>
              </Button>
            ) : null
          }
          rightHeaderActions={
            <TotalCountField
              label="Ukupno rezervisanih terapija:"
              count={totalReserved}
              loading={listLoading}
            />
          }
          tableSearch={tableSearch}
          tertiarySearch={{
            type: 'custom',
            value: selectedMedications,
            onChange: setSelectedMedications,
            className: 'therapyFilterMedication',
            render: () => (
              <MedicationFilterSelect
                value={selectedMedications}
                onChange={setSelectedMedications}
                match={medicationMatch}
                onMatchChange={setMedicationMatch}
              />
            ),
          }}
          quaternarySearch={dateFilter}
          customTable={
            <ReservedTherapiesTable
              data={patients}
              onPatientClick={handlePatientClick}
              emptyState={emptyState}
            />
          }
          showPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalResults={totalResults}
          paginationLoading={listLoading}
          onPageChange={setCurrentPage}
        />
      </ContentLayout>
      <ReservedTherapiesModal
        isOpen={isPatientModalOpen}
        onClose={handlePatientModalClose}
        patient={selectedPatient}
      />
    </>
  );
}
